/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Apr 19 2024
 **/

import { _T, _P } from "@rfice/language";

import { GNBNotificationItem, NotificationItem } from "./atom";
import { AlarmLogMessage } from "./types";
import { usePersistActions } from "../../persistence";

export type AvatarReturnType = Required<GNBNotificationItem["avatar"]>;
export function formatGroupInfoByAvatar(info: PushServices.GroupInfo): AvatarReturnType {
	return {
		type: "group",
		name: info.name,
		imageSrc: info.logoPath,
		colorLogo: info.colorLogo && `${info.colorLogo}_50_100`
	};
}

export function formatSpaceInfoByAvatar(info: PushServices.SpaceInfo): AvatarReturnType {
	return {
		type: "space",
		name: info.name?.slice(0, 1) || "",
		imageSrc: info.thumbnailPath,
		colorLogo: undefined
	};
}

export function formatUserInfoByAvatar(info: PushServices.UserInfo): AvatarReturnType {
	return {
		type: "user",
		name: info.name?.slice(0, 1) || "",
		imageSrc: undefined,
		colorLogo: undefined
	};
}

// export function formatGNBAlarmData(message: NotificationItem): GNBNotificationItem | undefined {
export function formatGNBAlarmData(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: NotificationItem
): GNBNotificationItem | undefined {
	switch (message.type) {
		case AlarmLogMessage.SPACE_JOIN_REQUEST: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo?.name || "",
				content: _P("{{name}}님이 스페이스 가입 요청하였습니다.", {
					name: `<span id="target">${payload.userInfo.name}</span>`
				}),
				id: payload.readKey,
				meta: {
					type,
					spaceInfo: payload.spaceInfo
				},
				avatar: formatSpaceInfoByAvatar(payload.spaceInfo),
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.SPACE_JOIN_REQUEST_CANCEL: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo.name,
				content: _P("{{name}}님이 가입 요청을 취소하였습니다.", {
					name: `<span id="target">${payload.userInfo.name}</span>`
				}),
				id: payload.readKey,
				meta: {
					type,
					spaceInfo: payload.spaceInfo
				},
				avatar: formatSpaceInfoByAvatar(payload.spaceInfo),
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.SPACE_ACCEPT_JOIN_REQUEST: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo.name,
				content: _T("스페이스 가입이 완료되었습니다."),
				id: payload.readKey,
				meta: {
					type,
					spaceInfo: payload.spaceInfo
				},
				avatar: formatSpaceInfoByAvatar(payload.spaceInfo),
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.SPACE_JOINED: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo.name,
				content: _T("스페이스 가입이 완료되었습니다."),
				id: payload.readKey,
				meta: {
					type,
					spaceInfo: payload.spaceInfo
				},
				avatar: formatSpaceInfoByAvatar(payload.spaceInfo),
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.GROUP_JOIN_REQUEST: {
			const { payload, type } = message;
			const persistActions = usePersistActions();
			const infoUser = persistActions.users.get_user(payload?.userInfo?.id);

			return {
				title: payload.groupInfo.name,
				content: _P("{{name}}님이 그룹 가입 요청하였습니다.", {
					name: `<span id="target">${infoUser?.username || ""}</span>`
				}),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					groupInfo: payload.groupInfo
				},
				avatar: formatGroupInfoByAvatar(payload.groupInfo),
				isRead: message.read
			};
		}
		case AlarmLogMessage.GROUP_ACCEPT_JOIN_REQUEST: {
			const { payload, type } = message;
			return {
				title: payload.groupInfo.name,
				content: _T("그룹 가입이 완료되었습니다."),
				id: payload.readKey,
				meta: {
					type,
					groupInfo: payload.groupInfo
				},
				avatar: formatGroupInfoByAvatar(payload.groupInfo),
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.GROUP_USER_DELETED_BY_ADMIN: {
			const { payload, type } = message;
			return {
				title: payload.groupInfo.name,
				content: _T("관리자에 의해 그룹 멤버에서 제외되었습니다."),
				id: payload.readKey,
				meta: {
					type,
					groupInfo: payload.groupInfo
				},
				avatar: formatGroupInfoByAvatar(payload.groupInfo),
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.GROUP_JOINED: {
			const { payload, type } = message;
			return {
				title: payload.groupInfo.name,
				content: _T("그룹 가입이 완료되었습니다."),
				id: payload.readKey,
				meta: {
					type,
					groupInfo: payload.groupInfo
				},
				avatar: formatGroupInfoByAvatar(payload.groupInfo),
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.SPACE_INVITED: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo.name,
				content: _P("{{name}}님이 스페이스 멤버로 초대합니다.", {
					name: `<span id="target">${payload.sender.name}</span>`
				}),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					spaceInfo: payload.spaceInfo
				},
				avatar: formatSpaceInfoByAvatar(payload.spaceInfo),
				isRead: message.read
			};
		}
		case AlarmLogMessage.GROUP_INVITED: {
			const { payload, type } = message;
			return {
				title: payload.groupInfo.name,
				content: _P("{{name}}님이 그룹 멤버로 초대합니다.", {
					name: `<span id="target">${payload.sender.name}</span>`
				}),
				inviteMessage: payload.inviteMessage,
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					groupInfo: payload.groupInfo
				},
				avatar: formatGroupInfoByAvatar(payload.groupInfo),
				isRead: message.read
			};
		}
		case AlarmLogMessage.GROUP_ADMIN_ASSIGNED: {
			const { payload, type } = message;
			return {
				title: payload.groupInfo.name,
				content: _T("그룹 관리자로 지정되었습니다."),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					groupInfo: payload.groupInfo
				},
				isRead: message.read,
				avatar: formatGroupInfoByAvatar(payload.groupInfo)
			};
		}
		case AlarmLogMessage.SPACE_ADMIN_ASSIGNED: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo.name,
				content: _T("스페이스 관리자로 지정되었습니다."),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					spaceInfo: payload.spaceInfo
				},
				isRead: message.read,
				avatar: formatSpaceInfoByAvatar(payload.spaceInfo)
			};
		}
		case AlarmLogMessage.FRIEND_REQUESTED: {
			const { payload, type } = message;
			return {
				title: _T("친구 맺기"),
				content: _P("{{name}}님과 친구가 되었습니다.", {
					name: `<span id="target">${payload.userInfo.name}</span>`
				}),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					userInfo: payload.userInfo
				},
				avatar: formatUserInfoByAvatar(payload.userInfo),
				isRead: message.read
			};
		}
		case AlarmLogMessage.GUEST_BOOK_POSTED: {
			const { payload, type } = message;
			return {
				title: _T("방명록 새 글"),
				content: _P("{{name}}님이 방명록을 등록하였습니다.", {
					name: `<span id="target">${payload.userInfo.name}</span>`
				}),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					userInfo: payload.userInfo
				},
				avatar: formatUserInfoByAvatar(payload.userInfo),
				isRead: message.read
			};
		}
		case AlarmLogMessage.SPACE_USER_DELETED_BY_ADMIN: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo.name,
				content: _T("관리자에 의해 스페이스 멤버에서 제외되었습니다."),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					spaceInfo: payload.spaceInfo
				},
				avatar: formatSpaceInfoByAvatar(payload.spaceInfo),
				isRead: message.read
			};
		}
		case AlarmLogMessage.SPACE_INVITE_PROCESSED: {
			const { payload, type } = message;
			if (message.payload.approved) {
				return {
					title: payload.spaceInfo.name,
					content: _P("{{name}}님이 스페이스 멤버가 되었습니다.", {
						name: `<span id="target">${payload.userInfo.name}</span>`
					}),
					createdAt: message.createdTime,
					id: payload.readKey,
					meta: {
						type,
						spaceInfo: payload.spaceInfo
					},
					approved: message.payload.approved,
					isRead: message.read,
					avatar: formatSpaceInfoByAvatar(payload.spaceInfo)
				};
			} else {
				return {
					title: payload.spaceInfo.name,
					content: _P("{{name}}님이 스페이스 멤버 요청을 거절하였습니다.", {
						name: `<span id="target">${payload.userInfo.name}</span>`
					}),
					createdAt: message.createdTime,
					id: payload.readKey,
					meta: {
						type,
						spaceInfo: payload.spaceInfo
					},
					approved: message.payload.approved,
					isRead: message.read,
					avatar: formatSpaceInfoByAvatar(payload.spaceInfo)
				};
			}
		}
		case AlarmLogMessage.GROUP_INVITE_PROCESSED: {
			const { payload, type } = message;
			if (message.payload.approved) {
				return {
					title: payload.groupInfo.name,
					content: _P("{{name}}님이 그룹 멤버가 되었습니다.", {
						name: `<span id="target">${payload.userInfo.name}</span>`
					}),
					createdAt: message.createdTime,
					id: payload.readKey,
					meta: {
						type,
						groupInfo: payload.groupInfo
					},
					approved: message.payload.approved,
					isRead: message.read,
					avatar: formatGroupInfoByAvatar(payload.groupInfo)
				};
			} else {
				return {
					title: payload.groupInfo.name,
					content: _P("{{name}}님이 그룹 멤버 요청을 거절하였습니다.", {
						name: `<span id="target">${payload.userInfo.name}</span>`
					}),
					createdAt: message.createdTime,
					id: payload.readKey,
					meta: {
						type,
						groupInfo: payload.groupInfo
					},
					approved: message.payload.approved,
					isRead: message.read,
					avatar: formatGroupInfoByAvatar(payload.groupInfo)
				};
			}
		}
		case AlarmLogMessage.GROUP_INVITE_PROCESS: {
			const { payload, type } = message;
			return {
				title: payload.groupInfo.name,
				content: _P("{{name}}그룹 가입이 완료되었습니다.", {
					name: `<span id="target">${payload.userInfo.name}</span>`
				}),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					groupInfo: payload.groupInfo
				},
				isRead: message.read,
				avatar: formatGroupInfoByAvatar(payload.groupInfo)
			};
		}
		case AlarmLogMessage.SPACE_DELETED: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo.name,
				content: _T("관리자에 의해 스페이스가 삭제되어, 스페이스 입장 및 채널 이용이 불가합니다."),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					spaceInfo: payload.spaceInfo
				},
				avatar: formatSpaceInfoByAvatar(payload.spaceInfo),
				isRead: message.read
			};
		}
		case AlarmLogMessage.OPEN_WORLD_BLOCKED_USER_PARTICIPATED: {
			const { payload, type } = message;
			return {
				title: payload.spaceInfo.name,
				content: _P(
					"오픈월드 내 차단한 사용자가 있습니다.<br>그룹 및 친구 목록에서 차단한 사용자를 확인해주세요."
				),
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: { type },
				isRead: message.read
			};
		}

		case AlarmLogMessage.HANGUP_NO_RESPONSE_CALL: {
			const { payload, type } = message;

			return {
				title: payload.notificationCategory,
				content: payload.type,
				createdAt: message.createdTime,
				id: payload.readKey,
				meta: {
					type,
					callId: payload.callId
				},

				isRead: message.read,
				users: payload.users
			};
		}
		case AlarmLogMessage.KNOCK: {
			const { payload } = message;
			return {
				title: payload.userInfo.name,
				content: _T("노크를 남겼습니다."),
				createdAt: message.createdTime,
				id: payload.readKey,
				isRead: message.read,
				avatar: {
					name: payload.userInfo.name,
					imageSrc: payload.userInfo.profileImagePath,
					type: "knock",
					colorLogo: payload.userInfo.colorProfile
				}
			};
		}
		case AlarmLogMessage.MEETING_CONFERENCE_INVITE:
		case AlarmLogMessage.MEETING_SCHEDULE_INVITE: {
			const { payload, type } = message;
			return {
				title: payload.title || "",
				content: _P("{{name}}님이 미팅에 초대합니다.", {
					name: `<span id="target">${payload.senderName}</span>`
				}),
				id: payload.readKey,
				meta: {
					type
				},
				avatar: {
					type: "user",
					name: payload.senderName?.slice(0, 1) || "",
					// Update 06/02/2025: Notification: Profile image does not appear when receiving a meeting invitation notification
					imageSrc: payload?.userInfo?.profileImagePath,
					colorLogo: payload?.userInfo?.colorProfile
				},
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.MEETING_SCHEDULE_REMIND: {
			const { payload, type } = message;
			return {
				title: payload.title || "",
				content: _P("미팅 시작 {{minutes}}분 전 입니다.", {
					minutes: `<span id="target">${10}</span>`
				}),
				id: payload.readKey,
				meta: {
					type
				},
				avatar: {
					type: "group",
					name: payload.title || "",
					imageSrc: undefined,
					colorLogo: undefined
				},
				createdAt: message.createdTime,
				isRead: message.read
			};
		}
		case AlarmLogMessage.NOTICE_NOTIFY: {
			const { payload } = message;
			return {
				title: "Rfice",
				content: payload.title,
				createdAt: message.createdTime,
				id: payload.readKey,
				isRead: message.read,
				link: payload.link
			};
		}
	}
}

/**
 *
 * @param res RestAPI에서 받아온 직렬화된 Notification Normal Type의 Data
 * @returns 직렬화된 Notification Data를 NotificationItem으로 변환
 */
export function formatSerializedNormalData(res: {
	createdTime: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	payload: any;
	read: boolean;
	type: string; // keyof AlarmLogMessageValueType;
}): NotificationItem {
	return {
		...res,
		createdTime: new Date(res.createdTime),
		type: AlarmLogMessage[res.type as keyof typeof AlarmLogMessage]
	};
}
